import type { DayId, DayKind } from './calendar.ts'
import { baseDayKind } from './calendar.ts'
import type {
  Adaptations,
  GameId,
  Moment,
  RewardLadder,
  Rung,
  ScreenLimits,
  Step,
  StoredEvent,
  TaskKind,
} from './events.ts'
import type { FamilyId, MemberId, PhotoId, PurchaseId, RewardId, StepId, TaskId } from './ids.ts'

/**
 * The state of the house, derived by replaying the log. Nothing here is stored;
 * it is rebuilt from events, which is why the log is the only thing that has to
 * survive a backup or a broken tablet.
 */

export interface Family {
  readonly id: FamilyId
  readonly timezone: string
  readonly dayCutoffHour: number
  readonly meetingDayOfWeek: number
}

export interface Member {
  readonly id: MemberId
  readonly name: string
  readonly role: 'child' | 'parent'
  readonly birthYear: number | undefined
  readonly accent: string
  readonly emoji: string | undefined
  /** Cómo llama este niño a sus monedas. Sin poner, «monedas». */
  readonly coinWord: string | undefined
  /** A qué horas se le recuerda lo que toca. Vacío, no se le recuerda. */
  readonly reminders: readonly { readonly hora: string; readonly previo: boolean }[]
  readonly adaptations: Adaptations
  readonly limits: ScreenLimits
  readonly glutenFree: boolean
  /** Only ever goes up. */
  readonly xp: number
  /** Only ever goes down through a purchase. */
  readonly coins: number
  /**
   * Fuera de las listas, sin borrar nada.
   *
   * Hace falta porque importar una copia en una tablet que ya tenía casa deja
   * miembros duplicados, y hasta ahora no había manera de quitarlos de en medio:
   * la única salida era empezar de cero. La historia se queda —es un registro de
   * lo que pasó, no una lista de personas— y lo que cambia es a quién se enseña.
   */
  readonly archived: boolean
}

export interface Task {
  readonly id: TaskId
  readonly memberId: MemberId
  readonly kind: TaskKind
  readonly moment: Moment | undefined
  readonly name: string
  readonly emoji: string | undefined
  readonly steps: readonly Step[]
  readonly difficulty: number
  readonly rewards: RewardLadder
  readonly promotionThreshold: number
  readonly requiresPhoto: boolean
  readonly referencePhotoId: PhotoId | undefined
  readonly rung: Rung
  /** Si la da por hecha un juego, cuál. Sin poner, la comprueba una persona. */
  readonly game: GameId | undefined
  readonly archived: boolean
  /** The last day its wording or its steps changed. Used to warn a child who
   *  needs changes announced rather than discovered. */
  readonly changedOn: DayId | undefined
}

export interface Reward {
  readonly id: RewardId
  readonly name: string
  readonly price: number
  readonly kind: 'screen' | 'privilege' | 'family' | 'social' | 'big'
  readonly memberIds: readonly MemberId[]
  readonly notes: string | undefined
  readonly limit: { readonly per: 'day' | 'week'; readonly max: number } | undefined
  readonly active: boolean
}

export type TaskStatus = 'pending' | 'marked' | 'validated'

export interface TaskDay {
  readonly steps: ReadonlySet<StepId>
  readonly status: TaskStatus
  readonly photoId: PhotoId | undefined
  readonly coins: number
  readonly xp: number
  /** Set when a parent sent it back. Cleared the moment it is marked again. */
  readonly returnedNote: string | undefined
  /**
   * How many times this task has fallen back to pending today, after having
   * been marked or validated. It exists so that "I am done" can be pressed
   * again: the guard against a double tap keys off this number, and a task that
   * legitimately comes back down gets a fresh key instead of a dead button.
   */
  readonly attempt: number
}

export interface MemberDay {
  readonly tasks: ReadonlyMap<TaskId, TaskDay>
  readonly chestOpened: boolean
  readonly chestMinutes: number
  /** Minutes of screen time bought today, for the daily cap. */
  readonly extraMinutes: number
  readonly paused: boolean
  readonly timerEndsAt: string | undefined
  readonly timerMinutes: number
}

export interface Day {
  readonly kind: DayKind
  readonly members: ReadonlyMap<MemberId, MemberDay>
}

export type PurchaseStatus = 'pending' | 'granted' | 'delivered' | 'cancelled'

export interface Purchase {
  readonly id: PurchaseId
  readonly memberId: MemberId
  readonly rewardId: RewardId
  readonly day: DayId
  readonly price: number
  readonly quantity: number
  readonly status: PurchaseStatus
  readonly grantedMinutes: number
}

export interface FamilyState {
  readonly family: Family | undefined
  readonly members: ReadonlyMap<MemberId, Member>
  readonly tasks: ReadonlyMap<TaskId, Task>
  readonly rewards: ReadonlyMap<RewardId, Reward>
  readonly days: ReadonlyMap<DayId, Day>
  readonly purchases: ReadonlyMap<PurchaseId, Purchase>
  /** Every command that produced events, so replaying one twice is a no-op. */
  readonly seenCommands: ReadonlySet<string>
  readonly eventCount: number
}

export const EMPTY_STATE: FamilyState = {
  family: undefined,
  members: new Map(),
  tasks: new Map(),
  rewards: new Map(),
  days: new Map(),
  purchases: new Map(),
  seenCommands: new Set(),
  eventCount: 0,
}

export const EMPTY_MEMBER_DAY: MemberDay = {
  tasks: new Map(),
  chestOpened: false,
  chestMinutes: 0,
  extraMinutes: 0,
  paused: false,
  timerEndsAt: undefined,
  timerMinutes: 0,
}

export const EMPTY_TASK_DAY: TaskDay = {
  steps: new Set(),
  status: 'pending',
  photoId: undefined,
  coins: 0,
  xp: 0,
  returnedNote: undefined,
  attempt: 0,
}

// --- Read helpers ------------------------------------------------------------

export function dayOf(state: FamilyState, day: DayId): Day {
  return state.days.get(day) ?? { kind: baseDayKind(day), members: new Map() }
}

export function memberDayOf(state: FamilyState, day: DayId, memberId: MemberId): MemberDay {
  return dayOf(state, day).members.get(memberId) ?? EMPTY_MEMBER_DAY
}

export function taskDayOf(
  state: FamilyState,
  day: DayId,
  memberId: MemberId,
  taskId: TaskId,
): TaskDay {
  return memberDayOf(state, day, memberId).tasks.get(taskId) ?? EMPTY_TASK_DAY
}

/**
 * Los hijos que se enseñan, en el orden en que se dieron de alta.
 *
 * Existe para que filtrar por archivados no dependa de que cada pantalla se
 * acuerde: había ocho sitios repitiendo `role === 'child'`, y a la novena se
 * olvida.
 */
export function childrenOf(state: FamilyState): readonly Member[] {
  return [...state.members.values()].filter((m) => m.role === 'child' && !m.archived)
}

/** Los adultos de la casa. Puede haber uno, dos o los que hagan falta. */
export function adultsOf(state: FamilyState): readonly Member[] {
  return [...state.members.values()].filter((m) => m.role === 'parent' && !m.archived)
}

/** Active tasks belonging to a member, in a stable order. */
export function tasksOf(state: FamilyState, memberId: MemberId, kind?: TaskKind): readonly Task[] {
  return [...state.tasks.values()]
    .filter(
      (t) => t.memberId === memberId && !t.archived && (kind === undefined || t.kind === kind),
    )
    .sort((a, b) => a.name.localeCompare(b.name, 'es'))
}

export function routinesOf(
  state: FamilyState,
  memberId: MemberId,
  moment: Moment,
): readonly Task[] {
  return tasksOf(state, memberId, 'routine').filter((t) => t.moment === moment)
}

export type { StoredEvent }
